/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
import { loadCompositionEstimates } from "components/jiffy/products/product-composition-estimates";
import initProductCardV3 from "components/jiffy/product-card-v3/product-card-v3";
import { isElementVisible } from "common/utils/ui/visibility";
import { CachedDeliveryManager } from "common/utils/delivery-date/cached-delivery-manager";

const OBSERVER_ROOT_MARGIN = "100px";
const OBSERVER_THRESHOLD = 0.1;
const PRODUCT_CARD_SELECTOR = ".js-product-card-v3";

/**
 * Manages product cards in a grid layout with lazy loading functionality.
 * Handles intersection observation to load composition estimates as cards become visible.
 */
class ProductsGrid {
  #productCardSelector = PRODUCT_CARD_SELECTOR;

  #processedIds;

  #observer;

  #beforeUnloadHandler;

  #gridOptions;

  /**
   * Creates a new ProductsGridClass instance.
   */
  constructor(productCardSelector = PRODUCT_CARD_SELECTOR) {
    this.#processedIds = new Set();
    this.#observer = null;
    this.#beforeUnloadHandler = null;
    this.#gridOptions = null;
    this.#productCardSelector = productCardSelector;

    // Check for made category
    const $container = document.querySelector(".js-made-category");
    if (!$container) return;
    const { isMadeCategoryPage } = $container.dataset;

    this.#gridOptions = {
      isMade: isMadeCategoryPage,
      isProductCardV3: true
    };
  }

  /**
   * Gets the Set of processed composition IDs.
   * @returns {Set<string>} Set of composition IDs that have been processed
   */
  get processedIds() {
    return this.#processedIds;
  }

  /**
   * Gets the NodeList of product cards.
   * @returns {NodeListOf<Element>} NodeList of product card elements
   */
  get #$cards() {
    return document.querySelectorAll(this.#productCardSelector);
  }

  /**
   * Initializes the product grid by setting up cards, loading visible compositions,
   * and configuring lazy loading.
   */
  initialize() {
    if (!this.#$cards.length) return;

    initProductCardV3(this.#$cards);

    this.#loadVisibleCompositions();
    this.#setupLazyLoading();

    this.#beforeUnloadHandler = () => this.destroy();
    window.addEventListener("beforeunload", this.#beforeUnloadHandler);
  }

  /**
   * Loads composition estimates for visible cards that haven't been processed yet.
   * @private
   */
  #loadVisibleCompositions() {
    const compositionIds = this.#getVisibleCompositionIds();
    if (compositionIds.length) {
      if (this.#gridOptions.isMade) {
        // Send individual requests for each composition ID when isMade is true
        compositionIds.forEach(compositionId => {
          loadCompositionEstimates([compositionId], this.#gridOptions).catch(error => {
            console.error(error);
          });
        });
      } else {
        // Use batch strategy when isMade is false
        loadCompositionEstimates(compositionIds, this.#gridOptions).catch(error => {
          console.error(error);
        });
      }
    }
  }

  /**
   * Gets composition IDs of visible cards that haven't been processed yet.
   * @returns {string[]} Array of composition IDs
   * @private
   */
  #getVisibleCompositionIds() {
    const visibleIds = [];

    this.#$cards.forEach($card => {
      if (isElementVisible($card)) {
        const { compositionId } = $card.dataset;

        if (compositionId && !this.#processedIds.has(compositionId)) {
          const hasCachedDelivery =
            $card.dataset.hasCachedDelivery === "true" ||
            $card.querySelector('[data-has-cached-delivery="true"]');

          if (hasCachedDelivery && window.jiffyCachedDeliveryData?.deliveryDate) {
            this.#processedIds.add(compositionId);
            // Apply cached delivery date immediately
            ProductsGrid.#applyCachedDeliveryDate($card);
          } else if ($card.querySelector(".js-plp-delivery-date")) {
            // Only load composition estimates if we don't have cached data
            this.#processedIds.add(compositionId);
            visibleIds.push(compositionId);
          }
        }
      }
    });

    return visibleIds;
  }

  /**
   * Sets up IntersectionObserver for lazy loading cards as they scroll into view.
   * @private
   */
  #setupLazyLoading() {
    const observerOptions = {
      root: null,
      rootMargin: OBSERVER_ROOT_MARGIN,
      threshold: OBSERVER_THRESHOLD
    };

    this.#observer = new IntersectionObserver(this.#handleIntersection.bind(this), observerOptions);

    this.#observeUnprocessedCards();
  }

  /**
   * Starts observing unprocessed cards for intersection.
   * @private
   */
  #observeUnprocessedCards() {
    this.#$cards.forEach($card => {
      const { compositionId } = $card.dataset;
      if (compositionId && !this.#processedIds.has(compositionId)) {
        this.#observer.observe($card);
      }
    });
  }

  /**
   * Handles intersection events when cards become visible.
   * @param {IntersectionObserverEntry[]} entries - Array of intersection observer entries
   * @private
   */
  #handleIntersection($entries) {
    const compositionIdsToLoad = [];

    $entries.forEach($entry => {
      if ($entry.isIntersecting) {
        const { compositionId } = $entry.target.dataset;

        if (compositionId && !this.#processedIds.has(compositionId)) {
          const hasCachedDelivery =
            $entry.target.dataset.hasCachedDelivery === "true" ||
            $entry.target.querySelector('[data-has-cached-delivery="true"]');

          if (hasCachedDelivery && window.jiffyCachedDeliveryData?.deliveryDate) {
            // Apply cached delivery date immediately
            this.#processedIds.add(compositionId);
            ProductsGrid.#applyCachedDeliveryDate($entry.target);
          } else if ($entry.target.querySelector(".js-plp-delivery-date")) {
            // Only load composition estimates if we don't have cached data
            compositionIdsToLoad.push(compositionId);
            this.#processedIds.add(compositionId);
          }

          this.#observer.unobserve($entry.target);
        }
      }
    });

    if (compositionIdsToLoad.length) {
      if (this.#gridOptions.isMade) {
        // Send individual requests for each composition ID when isMade is true
        compositionIdsToLoad.forEach(compositionId => {
          loadCompositionEstimates([compositionId], this.#gridOptions).catch(error => {
            console.error(error);
          });
        });
      } else {
        // Use batch strategy when isMade is false
        loadCompositionEstimates(compositionIdsToLoad, this.#gridOptions).catch(error => {
          console.error(error);
        });
      }
    }
  }

  /**
   * Applies cached delivery date to a product card.
   * @param {Element} $card - The product card element
   * @private
   */
  static #applyCachedDeliveryDate($card) {
    const $deliveryDateElements = $card.querySelectorAll(".js-plp-delivery-date");

    const setLoadingState = isLoading => {
      const $loadingEl = $card.querySelector(".js-plp-delivery-loading");
      const $loadedEl = $card.querySelector(".js-plp-delivery-loaded");

      if ($loadingEl) {
        $loadingEl.classList.toggle("hidden", !isLoading);
      }
      if ($loadedEl) {
        $loadedEl.classList.toggle("hidden", isLoading);
        $loadedEl.classList.toggle("flex", !isLoading);
      }
    };

    CachedDeliveryManager.applyCachedDelivery({
      deliveryDateElements: $deliveryDateElements,
      setLoadingState
    });

    const $deliveryTimeleftTextEl = $card.querySelector(".js-plp-delivery-timeleft-text");
    if ($deliveryTimeleftTextEl) {
      CachedDeliveryManager.applyCachedTimeLeftText(
        $deliveryTimeleftTextEl,
        window.jiffyCachedDeliveryData.hasDeliveryStartEnd,
        window.jiffyCachedDeliveryData.cutoffTime,
        true
      );
    }
  }

  /**
   * Cleans up resources by disconnecting observer and clearing data.
   */
  destroy() {
    if (this.#observer) {
      this.#observer.disconnect();
      this.#observer = null;
    }
    this.#processedIds.clear();
    this.#gridOptions = null;

    if (this.#beforeUnloadHandler) {
      window.removeEventListener("beforeunload", this.#beforeUnloadHandler);
      this.#beforeUnloadHandler = null;
    }
  }
}

export default () => new ProductsGrid().initialize();
